import { Permission } from '@/api/permissions';

const EMPTY_PERMISSION_SET: ReadonlySet<Permission> = new Set<Permission>();

/**
 * Cache of lookup sets keyed on the permission array identity handed out by the
 * React Query cache. A WeakMap means the entry dies with the cached profile.
 */
const permissionSetCache = new WeakMap<readonly Permission[], ReadonlySet<Permission>>();

/**
 * Builds — and memoises across the whole app — an O(1) lookup set for a
 * permission list. Every gate in the tree shares a single Set per profile
 * fetch instead of rebuilding a 200+ member Set per component.
 *
 * `undefined` (profile still loading, or failed) yields an empty set, so the
 * deny-by-default behaviour of the permission gates falls out of the data
 * shape rather than relying on call-site checks.
 */
export const getPermissionSet = (
  permissions: readonly Permission[] | undefined,
): ReadonlySet<Permission> => {
  if (!permissions) return EMPTY_PERMISSION_SET;

  const cached = permissionSetCache.get(permissions);
  if (cached) return cached;

  const set: ReadonlySet<Permission> = new Set(permissions);
  permissionSetCache.set(permissions, set);

  return set;
};
